import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format lamports to SOL with adaptive decimal places
 * Uses more decimals for smaller amounts to avoid losing precision
 */
export function formatLamports(lamports: string | number | bigint): string {
  const sol = Number(lamports) / 1e9; // Convert lamports to SOL

  // Adaptive decimals based on magnitude
  if (sol === 0) return '0';
  if (sol >= 1) return sol.toFixed(2);      // 1+ SOL: 2 decimals (1.23 SOL)
  if (sol >= 0.01) return sol.toFixed(3);   // 0.01-1: 3 decimals (0.123 SOL)
  if (sol >= 0.001) return sol.toFixed(4);  // 0.001-0.01: 4 decimals (0.0012 SOL)
  return sol.toFixed(6);                     // < 0.001: 6 decimals (0.000123 SOL)
}

/**
 * Format token amount based on decimals
 */
export function formatTokenAmount(amount: string | number, decimals: number): string {
  const num = Number(amount) / Math.pow(10, decimals);
  return num.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

/**
 * Format date as relative time (e.g., "2m ago", "5h ago")
 */
export function formatTimeAgo(date: Date | string): string {
  const now = new Date();
  const target = typeof date === 'string' ? new Date(date) : date;
  const diff = now.getTime() - target.getTime();

  const minute = 60 * 1000;
  const hour = minute * 60;
  const day = hour * 24;

  if (diff < minute) {
    return 'just now';
  } else if (diff < hour) {
    const minutes = Math.floor(diff / minute);
    return `${minutes}m ago`;
  } else if (diff < day) {
    const hours = Math.floor(diff / hour);
    return `${hours}h ago`;
  } else {
    const days = Math.floor(diff / day);
    return `${days}d ago`;
  }
}

/**
 * Format timestamp as YYYY-MM-DD HH:MM:SS
 * @param date - Date object or ISO string
 * @param timezone - Optional timezone (defaults to local)
 * @returns Formatted timestamp string
 *
 * @example
 * formatTimestamp(new Date()) // "2025-10-21 14:23:45"
 * formatTimestamp("2025-10-21T14:23:45.123Z") // "2025-10-21 14:23:45"
 */
export function formatTimestamp(date: Date | string): string {
  const target = typeof date === 'string' ? new Date(date) : date;

  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const day = String(target.getDate()).padStart(2, '0');
  const hours = String(target.getHours()).padStart(2, '0');
  const minutes = String(target.getMinutes()).padStart(2, '0');
  const seconds = String(target.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

